import React, { useCallback, useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Box,
  Chip,
  Stack,
  Button,
  TextField,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
} from "recharts";
const lucy = require("../../../helpers/lucy_service");

const fmt = (n) => (n == null ? "-" : Number(n).toLocaleString("es-GT"));

const ConsumptionTab = ({ sub }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [quota, setQuota] = useState(null);
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [addTokens, setAddTokens] = useState("");
  const [newLimit, setNewLimit] = useState("");
  const [defLimit, setDefLimit] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        lucy.getUserStats(sub),
        lucy.adminListTokenRequests({ status: "pending" }).catch(() => null),
      ]);
      setStats(s.data.response.stats);
      setQuota(s.data.response.quota);
      const mine = (r && r.data.response.items) || [];
      setRequests(mine.filter((x) => x.userId === sub));
    } catch (e) {
      setMsg({ sev: "error", text: e.mensaje || "Error al cargar" });
    } finally {
      setLoading(false);
    }
  }, [sub]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      setMsg({ sev: "success", text: ok });
      setAddTokens("");
      setNewLimit("");
      setDefLimit("");
      setReason("");
      await load();
    } catch (e) {
      setMsg({ sev: "error", text: e.mensaje || (e.data && e.data.log) || "Error" });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <CircularProgress />;

  const consumed = quota ? Number(quota.consumed) : 0;
  const assigned = quota ? Number(quota.assigned) : 0;
  const pct = assigned ? Math.min(100, Math.round((consumed / assigned) * 100)) : 0;
  const daily = Object.entries(stats?.dailyTokens || {})
    .sort()
    .slice(-30)
    .map(([d, v]) => ({ dia: d.slice(5), tokens: v }));
  const tags = Object.entries(stats?.tagCounts || {}).sort((a, b) => b[1] - a[1]);

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Ventana de tokens activa
              </Typography>
              <Typography variant="h5">
                {fmt(consumed)} / {fmt(assigned)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={pct}
                color={pct >= 100 ? "error" : pct >= 80 ? "warning" : "primary"}
                sx={{ my: 1, height: 8, borderRadius: 4 }}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  label={quota ? quota.status : "sin cuota"}
                  color={
                    quota?.status === "active"
                      ? "success"
                      : quota?.status === "exhausted"
                      ? "error"
                      : "default"
                  }
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`por defecto: ${fmt(quota?.defaultLimit)}`}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Acumulado
              </Typography>
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <Typography variant="h6">{fmt(stats?.totalTokens)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    tokens totales
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6">{fmt(stats?.totalConversations)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    conversaciones
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h6">{fmt(stats?.totalMessages)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    mensajes
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    {stats?.lastActivityAt
                      ? new Date(stats.lastActivityAt).toLocaleString("es-GT")
                      : "sin actividad"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    última actividad
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Consumo por día (últimos 30)
              </Typography>
              <Box sx={{ height: 180, mt: 1 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={daily}>
                    <XAxis dataKey="dia" fontSize={11} />
                    <YAxis fontSize={11} width={48} />
                    <RTooltip />
                    <Bar dataKey="tokens" fill="#0f6b74" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Temas frecuentes
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                {tags.length ? (
                  tags.map(([t, c]) => (
                    <Chip key={t} size="small" label={`${t} (${c})`} />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    aún sin clasificar
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" sx={{ mb: 1 }}>
        Administrar cuota
      </Typography>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Ampliar (+ tokens)"
            type="number"
            value={addTokens}
            onChange={(e) => setAddTokens(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Nueva ventana (límite)"
            type="number"
            value={newLimit}
            onChange={(e) => setNewLimit(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Límite por defecto"
            type="number"
            value={defLimit}
            onChange={(e) => setDefLimit(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Motivo (opcional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Grid>
      </Grid>
      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}>
        <Button
          variant="contained"
          disabled={busy || !addTokens}
          onClick={() =>
            act(
              () => lucy.extendUserQuota(sub, { addTokens: Number(addTokens), reason }),
              "Cuota ampliada"
            )
          }
        >
          Ampliar tokens
        </Button>
        <Button
          variant="outlined"
          disabled={busy}
          onClick={() =>
            act(
              () =>
                lucy.openNewWindow(sub, {
                  limit: newLimit ? Number(newLimit) : undefined,
                  reason,
                }),
              "Nueva ventana de sesión abierta"
            )
          }
        >
          Nueva ventana de sesión
        </Button>
        <Button
          variant="outlined"
          disabled={busy || !defLimit}
          onClick={() =>
            act(
              () => lucy.setUserDefaultLimit(sub, Number(defLimit)),
              "Límite por defecto actualizado"
            )
          }
        >
          Cambiar límite por defecto
        </Button>
      </Stack>

      {requests.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Solicitudes de tokens pendientes
          </Typography>
          <Stack spacing={1}>
            {requests.map((r) => (
              <Alert
                key={r.requestedAt}
                severity="info"
                action={
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      disabled={busy}
                      onClick={() =>
                        act(
                          () =>
                            lucy.adminResolveTokenRequest(sub, r.requestedAt, {
                              action: "extend",
                              addTokens: Number(addTokens) || 100000,
                              reason: "solicitud aprobada",
                            }),
                          "Solicitud aprobada (ampliación)"
                        )
                      }
                    >
                      Aprobar (+{fmt(Number(addTokens) || 100000)})
                    </Button>
                    <Button
                      size="small"
                      color="inherit"
                      disabled={busy}
                      onClick={() =>
                        act(
                          () =>
                            lucy.adminResolveTokenRequest(sub, r.requestedAt, {
                              action: "reject",
                            }),
                          "Solicitud rechazada"
                        )
                      }
                    >
                      Rechazar
                    </Button>
                  </Stack>
                }
              >
                {new Date(r.requestedAt).toLocaleString("es-GT")} —{" "}
                {r.message || "(sin mensaje)"}
              </Alert>
            ))}
          </Stack>
        </Box>
      )}

      <Snackbar
        open={!!msg}
        autoHideDuration={4000}
        onClose={() => setMsg(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {msg ? (
          <Alert severity={msg.sev} onClose={() => setMsg(null)}>
            {msg.text}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  );
};

export default ConsumptionTab;
